import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Shield, AlertTriangle, Users, Eye, Flag } from "lucide-react";
import { useSEO, generateEducationalJsonLd } from '@/hooks/useSEO';

const FairPlayPolicy = () => {
  // SEO Configuration
  useSEO({
    title: "Fair Play Policy - ChessHatch Anti-Cheating Guidelines",
    description: "ChessHatch Fair Play Policy covering cheating prevention, ethical gameplay, reporting systems, and consequences. Learn about maintaining integrity in online chess.",
    keywords: "chess fair play, anti-cheating policy, chess integrity, chess engine detection, chess sportsmanship, chess ethics",
    jsonLd: generateEducationalJsonLd("ChessHatch Fair Play Policy", "Comprehensive guidelines for ethical chess gameplay and anti-cheating measures")
  });

  return (
    <ChessAppLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4 text-teen-green">Cheating and Fair Play Policy</h1>
          <p className="text-muted-foreground text-lg">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="space-y-6">
          <Card className="bg-teen-green/5 border-teen-green/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-teen-green" />
                Our Commitment to Fair Play
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p className="text-lg">
                At ChessHatch, we believe that chess is most enjoyable and educational when everyone plays fairly. 
                This policy outlines what constitutes fair play, what behaviors are prohibited, and how we maintain 
                a level playing field for all our young chess enthusiasts.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-teen-blue" />
                What is Fair Play?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>Fair play means:</p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="flex items-start gap-3 p-3 bg-teen-green/5 rounded-lg">
                  <div className="w-2 h-2 bg-teen-green rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-teen-green">Playing Your Own Games</h4>
                    <p className="text-sm text-muted-foreground">Every move should be your own decision</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-teen-blue/5 rounded-lg">
                  <div className="w-2 h-2 bg-teen-blue rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-teen-blue">Learning from Losses</h4>
                    <p className="text-sm text-muted-foreground">Every game is a chance to improve</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-teen-purple/5 rounded-lg">
                  <div className="w-2 h-2 bg-teen-purple rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-teen-purple">Respecting Opponents</h4>
                    <p className="text-sm text-muted-foreground">Treat every player with kindness</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-teen-gold/5 rounded-lg">
                  <div className="w-2 h-2 bg-teen-gold rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-teen-gold">Playing to Learn</h4>
                    <p className="text-sm text-muted-foreground">Focus on improvement, not just winning</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Prohibited Behaviors
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>The following actions are strictly forbidden on ChessHatch:</p>
              
              <div className="space-y-3">
                <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <h4 className="font-semibold text-destructive mb-2">Computer Assistance</h4>
                  <ul className="list-disc ml-6 text-sm space-y-1">
                    <li>Using chess engines (like Stockfish, Komodo, etc.) during games</li>
                    <li>Using analysis software to evaluate positions</li>
                    <li>Getting move suggestions from apps or websites</li>
                    <li>Using opening books or databases during play</li>
                  </ul>
                </div>

                <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <h4 className="font-semibold text-destructive mb-2">Human Assistance</h4>
                  <ul className="list-disc ml-6 text-sm space-y-1">
                    <li>Getting help from other players during games</li>
                    <li>Having someone else play moves for you</li>
                    <li>Consulting coaches or stronger players mid-game</li>
                    <li>Collaborating with others on your moves</li>
                  </ul>
                </div>

                <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <h4 className="font-semibold text-destructive mb-2">Account Violations</h4>
                  <ul className="list-disc ml-6 text-sm space-y-1">
                    <li>Creating multiple accounts to manipulate ratings</li>
                    <li>Sharing your account with others</li>
                    <li>Deliberately losing games to lower your rating</li>
                    <li>Boosting ratings through arranged wins</li>
                  </ul>
                </div>

                <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <h4 className="font-semibold text-destructive mb-2">Unsportsmanlike Conduct</h4>
                  <ul className="list-disc ml-6 text-sm space-y-1">
                    <li>Abandoning games without resigning</li>
                    <li>Stalling or wasting time deliberately</li>
                    <li>Using offensive language or behavior</li>
                    <li>Harassment or bullying of other players</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-teen-purple" />
                How We Detect Cheating
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>We use several methods to maintain fair play:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Statistical Analysis:</strong> We analyze playing patterns and move accuracy</li>
                <li><strong>Time Analysis:</strong> We monitor thinking time and consistency</li>
                <li><strong>Engine Correlation:</strong> We check if moves match computer suggestions</li>
                <li><strong>Community Reports:</strong> Players can report suspicious behavior</li>
                <li><strong>Manual Review:</strong> Our team reviews flagged games and accounts</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Consequences of Cheating</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>When we detect cheating, we may take the following actions:</p>
              
              <div className="grid gap-3 mt-4">
                <Badge variant="outline" className="p-3 justify-start border-orange-200 bg-orange-50">
                  <span className="font-semibold text-orange-700">Warning:</span>
                  <span className="ml-2 text-sm">First-time minor violations receive educational guidance</span>
                </Badge>
                
                <Badge variant="outline" className="p-3 justify-start border-red-200 bg-red-50">
                  <span className="font-semibold text-red-700">Temporary Restriction:</span>
                  <span className="ml-2 text-sm">Limited access to competitive features</span>
                </Badge>
                
                <Badge variant="outline" className="p-3 justify-start border-red-300 bg-red-100">
                  <span className="font-semibold text-red-800">Account Suspension:</span>
                  <span className="ml-2 text-sm">Temporary ban from the platform</span>
                </Badge>
                
                <Badge variant="outline" className="p-3 justify-start border-red-400 bg-red-200">
                  <span className="font-semibold text-red-900">Permanent Ban:</span>
                  <span className="ml-2 text-sm">For severe or repeated violations</span>
                </Badge>
              </div>
              
              <p className="mt-4">
                <strong>For young users:</strong> We prioritize education over punishment and will work with 
                parents/guardians to address violations constructively.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flag className="h-5 w-5 text-teen-gold" />
                Reporting Suspicious Behavior
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>If you suspect someone is cheating:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Email us at reports@chesshatch.com</strong> with details</li>
                <li>Include the player's username and specific games if possible</li>
                <li>Describe the suspicious behavior you noticed</li>
                <li>Don't accuse players publicly - let us investigate</li>
                <li>Continue playing fairly regardless of your opponent's behavior</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Appeals Process</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>If you believe you've been wrongly accused of cheating:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Contact our support team through the appeals process</li>
                <li>Provide any relevant information about your games</li>
                <li>Be patient while we review your case thoroughly</li>
                <li>For users under 18, parents/guardians may assist with appeals</li>
              </ul>
              <p className="mt-4">
                We take all appeals seriously and will investigate fairly and thoroughly.
              </p>
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <Card className="bg-teen-green/5 border-teen-green/20">
            <CardHeader>
              <CardTitle>Playing with Honor</CardTitle>
              <CardDescription>
                Chess is most rewarding when played with integrity
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Remember: The goal of chess is to learn, improve, and have fun. Cheating robs you of the joy 
                of genuine improvement and the satisfaction of real achievement. Play with honor, learn from 
                every game, and help make ChessHatch a fair and fun place for everyone!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </ChessAppLayout>
  );
};

export default FairPlayPolicy;